import {
  Button,
  Icon,
  Toast,
  type ToastType,
  type Column,
} from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  companies,
  contacts,
  offices,
  type Company,
  type CompanyCreateRequest,
  type CompanyUpdateRequest,
  type Contact,
  type Office,
  type NameTranslation,
} from "../../../api/addressBook.service";
import { crmWebhookLogs } from "../../../api/crm-webhook-logs.service.js";
import { deriveStashPrefill } from "./stashPrefill.js";
import { computeTranslationsDelta } from "../shared/translationsDelta";
import { DataTable } from "../../../components/DataTable/DataTable";
import { PageHeader } from "../../../components/PageHeader";
import PermissionGate from "../../../components/Permissions/PermissionGate";
import { usePermissions } from "../../../hooks/usePermissions";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import LoadingState from "../../../components/LoadingState";
import EmptyState from "../../../components/EmptyState";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { ConfirmationModal } from "../../../components/Modal/ConfirmationModal";
import { TranslationsList } from "../../../components/TranslationsList/TranslationsList";
import { ROUTES } from "../../../routes";
import styles from "../AddressBook.module.css";

import { ExternalReferencesPanel } from "../components/ExternalReferencesPanel";
import { CompanyRelationships } from "./CompanyRelationships";
import { BUSINESS_TYPE_OPTIONS } from "./businessTypes";
import { INDUSTRY_OPTIONS } from "./industries";

type TabId = "details" | "offices" | "contacts" | "ext-refs" | "relationships";

function CompanyDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = id === "new";
  const [searchParams] = useSearchParams();
  const stash = isNew ? searchParams.get("stash") : null;
  // A stashed CRM webhook log carries its own name plus the CRM entity number, so it
  // takes precedence over a name typed into the reference selector.
  const prefilledName = isNew && !stash ? (searchParams.get("name")?.trim() ?? "") : "";
  const [crmEntityNumber, setCrmEntityNumber] = useState<string | null>(null);
  const { hasAnyPermission } = usePermissions();
  const { hasFlag } = useFeatureFlags();
  const isLive = hasFlag("address-book-live");
  const canWrite = hasAnyPermission(["addressbook.write"]) && isLive;

  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>("details");

  const [companyOffices, setCompanyOffices] = useState<Office[]>([]);
  const [officesLoading, setOfficesLoading] = useState(false);
  const [officesError, setOfficesError] = useState<string | null>(null);

  const [companyContacts, setCompanyContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CompanyCreateRequest>(() => ({
    name: prefilledName,
    originalName: "",
    globalName: "",
    businessType: "",
    slug: "",
    website: "",
    industry: "",
    notes: "",
    translations: [],
  }));
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [translationsValid, setTranslationsValid] = useState(true);

  const initialTranslationsRef = useRef<NameTranslation[]>([]);

  useEffect(() => {
    if (!isNew && id) {
      loadCompany(id);
    }
  }, [id, isNew]);

  useEffect(() => {
    if (!isNew && id) {
      loadOffices(id);
    }
  }, [id, isNew]);

  useEffect(() => {
    if (!isNew && id) {
      loadCompanyContacts(id);
    }
  }, [id, isNew]);

  useEffect(() => {
    if (!isNew || !stash) return;

    async function loadStash() {
      try {
        const log = await crmWebhookLogs.getById(stash!);
        const prefill = deriveStashPrefill(log);
        setCrmEntityNumber(prefill.crmEntityNumber);
        if (prefill.suggestedName !== "") {
          setFormData(prev => ({ ...prev, name: prefill.suggestedName }));
        }
      } catch {
        // degrade: leave crmEntityNumber null and name empty
      }
    }

    loadStash();
  }, [isNew, stash]);

  async function loadCompany(companyId: string) {
    try {
      setLoading(true);
      setError(null);
      const response = await companies.getById(companyId);
      setCompany(response.data);
      const loadedTranslations = response.data.translations ?? [];
      initialTranslationsRef.current = loadedTranslations;
      setFormData({
        name: response.data.name,
        originalName: response.data.originalName || "",
        globalName: response.data.globalName || "",
        businessType: response.data.businessType || "",
        // The form edits the BARE slug — the API's update contract (same as
        // create) takes it without the firms/ prefix and prefixes on store.
        // Legacy rows stored without the prefix pass through unchanged.
        slug: (response.data.slug || "").replace(/^firms\//, ""),
        website: response.data.website || "",
        industry: response.data.industry || "",
        notes: response.data.notes || "",
        translations: loadedTranslations,
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load company"));
    } finally {
      setLoading(false);
    }
  }

  async function loadAllOfficesForCompany(companyId: string): Promise<Office[]> {
    const accumulated: Office[] = [];
    let offset = 0;
    let total = Infinity;
    while (accumulated.length < total) {
      const response = await offices.getAll({ companyId, limit: 500, offset });
      accumulated.push(...response.data);
      total = response.pagination.total;
      offset += response.data.length;
      if (response.data.length === 0) break;
    }
    return accumulated;
  }

  async function loadOffices(companyId: string) {
    try {
      setOfficesLoading(true);
      setOfficesError(null);
      const allOffices = await loadAllOfficesForCompany(companyId);
      setCompanyOffices(allOffices);
    } catch {
      setOfficesError("Failed to load offices.");
    } finally {
      setOfficesLoading(false);
    }
  }

  async function loadAllContactsForCompany(companyId: string): Promise<Contact[]> {
    const accumulated: Contact[] = [];
    let offset = 0;
    let total = Infinity;
    while (accumulated.length < total) {
      const response = await contacts.getAll({ companyId, limit: 500, offset });
      accumulated.push(...response.data);
      total = response.pagination.total;
      offset += response.data.length;
      if (response.data.length === 0) break;
    }
    return accumulated;
  }

  async function loadCompanyContacts(companyId: string) {
    try {
      setContactsLoading(true);
      setContactsError(null);
      const allContacts = await loadAllContactsForCompany(companyId);
      setCompanyContacts(allContacts);
    } catch {
      setContactsError("Failed to load contacts.");
    } finally {
      setContactsLoading(false);
    }
  }

  function validateMondaqValue(value: string): { error: string | null; warning: string | null } {
    const trimmed = value.trim();
    if (!/^\d+$/.test(trimmed) || parseInt(trimmed, 10) <= 0) {
      return { error: "Mondaq ID must be a positive integer.", warning: null };
    }
    if (trimmed.length < 6) {
      return { error: null, warning: "Mondaq IDs are usually 6+ digits — double-check." };
    }
    return { error: null, warning: null };
  }

  function handleInputChange(field: keyof CompanyCreateRequest, value: string) {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }

  function validateForm(): boolean {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters";
    }

    if (!formData.businessType) {
      errors.businessType = "Business type is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const currentTranslations = formData.translations ?? [];

      if (isNew) {
        const { translations } = computeTranslationsDelta([], currentTranslations);
        const payload: CompanyCreateRequest = {
          name: formData.name.trim(),
          originalName: formData.originalName?.trim() || null,
          globalName: formData.globalName?.trim() || null,
          businessType: formData.businessType?.trim() || null,
          slug: formData.slug?.trim() || null,
          website: formData.website?.trim() || null,
          industry: formData.industry?.trim() || null,
          notes: formData.notes?.trim() || null,
          translations: translations.length > 0 ? translations : undefined,
          ...(crmEntityNumber ? { crmEntityNumber } : {}),
        };
        await companies.create(payload);
        setToast({ message: "Company created successfully", type: "success" });
        navigate(ROUTES.addressBook.companies, { replace: true });
      } else if (id) {
        const { translations, removeTranslations } = computeTranslationsDelta(
          initialTranslationsRef.current,
          currentTranslations,
        );
        const payload: CompanyUpdateRequest = {
          name: formData.name.trim(),
          originalName: formData.originalName?.trim() || null,
          globalName: formData.globalName?.trim() || null,
          businessType: formData.businessType?.trim() || null,
          slug: formData.slug?.trim() || null,
          website: formData.website?.trim() || null,
          industry: formData.industry?.trim() || null,
          notes: formData.notes?.trim() || null,
          translations: translations.length > 0 ? translations : undefined,
          removeTranslations: removeTranslations.length > 0 ? removeTranslations : undefined,
        };
        const response = await companies.update(id, payload);
        setCompany(response.data);
        initialTranslationsRef.current = response.data.translations ?? currentTranslations;
        setFormData(prev => ({ ...prev, translations: initialTranslationsRef.current }));
        setToast({ message: "Company updated successfully", type: "success" });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save company";
      setToast({ message, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!id || isNew) return;
    setShowDeleteModal(true);
  }

  async function confirmDelete() {
    if (!id) return;
    setIsDeleting(true);
    try {
      await companies.delete(id);
      setShowDeleteModal(false);
      setToast({ message: "Company deleted successfully", type: "success" });
      navigate(ROUTES.addressBook.companies);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete company";
      setToast({ message, type: "error" });
    } finally {
      setIsDeleting(false);
    }
  }

  function handleBack() {
    navigate(ROUTES.addressBook.companies);
  }

  // Check for auth errors
  if (error) {
    const typedError = error as Error & { status?: number; code?: string };
    if (typedError.code === "FEATURE_DISABLED") {
      return (
        <div className={styles.detailWrapper}>
          <PageHeader title="Company Details" subtitle="View and edit company information" />
          <EmptyState
            variant="error"
            title="Feature not yet enabled"
            message="Company management is not yet enabled for this environment. Please contact your administrator."
            action={{
              label: "Back to Companies",
              onClick: handleBack,
            }}
          />
        </div>
      );
    }
    const isAuthError = typedError.status === 401 || typedError.status === 403;
    if (isAuthError) {
      return <UnauthorizedView />;
    }
  }

  if (loading) {
    return (
      <div className={styles.detailWrapper}>
        <PageHeader
          title={isNew ? "Create Company" : "Company Details"}
          subtitle={
            isNew ? "Add a new company to the address book" : "View and edit company information"
          }
        />
        <LoadingState message="Loading company..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.detailWrapper}>
        <PageHeader title="Company Details" subtitle="View and edit company information" />
        <EmptyState
          variant="error"
          title="Failed to load company"
          message={error.message}
          action={{
            label: "Back to Companies",
            onClick: handleBack,
          }}
        />
      </div>
    );
  }

  function renderTabs() {
    if (isNew) return null;

    const tabs: { id: TabId; label: string; count?: number }[] = [
      { id: "details", label: "Details" },
      { id: "offices", label: "Offices", count: companyOffices.length },
      { id: "contacts", label: "Contacts", count: companyContacts.length },
      { id: "ext-refs", label: "Ext. Refs" },
      { id: "relationships", label: "Relationships" },
    ];

    return (
      <div className={styles.tabs} role="tablist" aria-label="Company sections">
        {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              type="button"
              aria-selected={isActive}
              className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className={`${styles.tabBadge} ${isActive ? styles.tabBadgeActive : styles.tabBadgeInactive}`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  function renderDetailsTab() {
    return (
      <form onSubmit={handleSubmit} className={styles.formContainer}>
        <div className={styles.formGrid}>
          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="name">
              Name *
            </label>
            <input
              id="name"
              type="text"
              className={`${styles.formInput}${formErrors.name ? ` ${styles.formInputError}` : ""}`}
              value={formData.name}
              onChange={e => handleInputChange("name", e.target.value)}
              placeholder="Enter company name"
              disabled={!canWrite}
            />
            {formErrors.name && <span className={styles.errorText}>{formErrors.name}</span>}
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="originalName">
              Original Name
            </label>
            <input
              id="originalName"
              type="text"
              className={styles.formInput}
              value={formData.originalName || ""}
              onChange={e => handleInputChange("originalName", e.target.value)}
              placeholder="Enter original company name"
              disabled={!canWrite}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="globalName">
              Global Name
            </label>
            <input
              id="globalName"
              type="text"
              className={styles.formInput}
              value={formData.globalName || ""}
              onChange={e => handleInputChange("globalName", e.target.value)}
              placeholder="Enter global company name"
              disabled={!canWrite}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="businessType">
              Business Type *
            </label>
            <select
              id="businessType"
              className={`${styles.formInput}${formErrors.businessType ? ` ${styles.formInputError}` : ""}`}
              value={formData.businessType || ""}
              onChange={e => handleInputChange("businessType", e.target.value)}
              disabled={!canWrite}
            >
              <option value="">— Select business type —</option>
              {BUSINESS_TYPE_OPTIONS.map(o => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
            {formErrors.businessType && (
              <span className={styles.errorText}>{formErrors.businessType}</span>
            )}
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="slug">
              Slug (stored as firms/&lt;slug&gt;)
            </label>
            <input
              id="slug"
              type="text"
              className={styles.formInput}
              value={formData.slug || ""}
              onChange={e => handleInputChange("slug", e.target.value)}
              placeholder="e.g. acme-law-firm — without the firms/ prefix"
              disabled={!canWrite}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel} htmlFor="industry">
              Industry
            </label>
            <select
              id="industry"
              className={styles.formInput}
              value={formData.industry || ""}
              onChange={e => handleInputChange("industry", e.target.value)}
              disabled={!canWrite}
            >
              <option value="">— Select industry —</option>
              {INDUSTRY_OPTIONS.map(o => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>

          <div className={`${styles.formField} ${styles.formFieldFull}`}>
            <label className={styles.formLabel} htmlFor="website">
              Website
            </label>
            <input
              id="website"
              type="text"
              className={styles.formInput}
              value={formData.website || ""}
              onChange={e => handleInputChange("website", e.target.value)}
              placeholder="https://example.com"
              disabled={!canWrite}
            />
          </div>

          <div className={`${styles.formField} ${styles.formFieldFull}`}>
            <label className={styles.formLabel} htmlFor="notes">
              Notes
            </label>
            <textarea
              id="notes"
              className={`${styles.formInput} ${styles.formTextarea}`}
              value={formData.notes || ""}
              onChange={e => handleInputChange("notes", e.target.value)}
              placeholder="Add any additional notes..."
              disabled={!canWrite}
            />
          </div>

          {isNew && crmEntityNumber && (
            <div className={styles.formField}>
              <label className={styles.formLabel} htmlFor="crmEntityNumber">
                CRM Entity Number
              </label>
              <input
                id="crmEntityNumber"
                type="text"
                className={styles.formInput}
                value={crmEntityNumber}
                readOnly
                disabled
                aria-readonly="true"
              />
            </div>
          )}
        </div>

        <div
          className={`${styles.formField} ${styles.formFieldFull}`}
          style={{ marginTop: "1.5rem" }}
        >
          <label className={styles.formLabel}>
            Translations
            <span className={styles.helperText} style={{ marginLeft: "0.5rem" }}>
              (optional) Localised names for this company
            </span>
          </label>
          <TranslationsList
            translations={formData.translations ?? []}
            onChange={translations => setFormData(prev => ({ ...prev, translations }))}
            disabled={!canWrite}
            onValidityChange={setTranslationsValid}
          />
        </div>

        <div className={styles.formActions}>
          <Button variant="outlined" color="default" onClick={handleBack} disabled={isSubmitting}>
            {canWrite ? "Cancel" : "Back"}
          </Button>
          {isLive && (
            <PermissionGate permission="addressbook.write">
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSubmitting || !translationsValid}
              >
                {isSubmitting ? "Saving..." : isNew ? "Create Company" : "Save Changes"}
              </Button>
            </PermissionGate>
          )}
        </div>
      </form>
    );
  }

  function renderOfficesTab() {
    const officeColumns: Column<Office>[] = [
      {
        header: "Name",
        accessor: "name",
        render: (_value: unknown, row: Office) => (
          <span
            style={{ fontWeight: 600, cursor: "pointer", color: "var(--color-primary)" }}
            onClick={() => navigate(ROUTES.addressBook.officeDetail(String(row.id)))}
          >
            {row.name}
          </span>
        ),
      },
      {
        header: "City",
        accessor: "city",
        render: (value: unknown) => (value as string) || "-",
      },
      {
        header: "Country",
        accessor: "countryName",
        render: (value: unknown) => (value as string) || "-",
      },
      {
        header: "Phone",
        accessor: "phone",
        render: (value: unknown) => (value as string) || "-",
      },
      {
        header: "Head Office",
        accessor: "isHeadOffice",
        render: (value: unknown) => {
          if (!value) return "";
          return (
            <span
              style={{
                padding: "0.125rem 0.5rem",
                borderRadius: "9999px",
                fontSize: "0.75rem",
                fontWeight: 500,
                backgroundColor: "rgb(219, 234, 254)",
                color: "rgb(29, 78, 216)",
              }}
            >
              Head Office
            </span>
          );
        },
      },
      {
        header: "Updated",
        accessor: "updatedAt",
        render: (value: unknown) => new Date(value as string).toLocaleDateString(),
      },
      {
        header: "Actions",
        accessor: "id",
        render: (_value: unknown, row: Office) => (
          <div style={{ textAlign: "right" }}>
            {isLive && (
              <PermissionGate permission="addressbook.delete">
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={async e => {
                    e.stopPropagation();
                    if (!window.confirm("Are you sure you want to delete this office?")) return;
                    try {
                      await offices.delete(row.id);
                      setToast({ message: "Office deleted successfully", type: "success" });
                      if (id) loadOffices(id);
                    } catch (err) {
                      setToast({
                        message: err instanceof Error ? err.message : "Failed to delete office",
                        type: "error",
                      });
                    }
                  }}
                >
                  Delete
                </Button>
              </PermissionGate>
            )}
          </div>
        ),
      },
    ];

    return (
      <div className={styles.section}>
        {officesLoading ? (
          <LoadingState message="Loading offices..." />
        ) : officesError ? (
          <span className={styles.errorText}>{officesError}</span>
        ) : (
          <DataTable
            data={companyOffices}
            columns={officeColumns}
            onRowClick={office => navigate(ROUTES.addressBook.officeDetail(String(office.id)))}
            rowKey="id"
          />
        )}
      </div>
    );
  }

  function renderContactsTab() {
    const contactColumns: Column<Contact>[] = [
      {
        header: "Name",
        accessor: "name",
        render: (_value: unknown, row: Contact) => (
          <span
            style={{ fontWeight: 600, cursor: "pointer", color: "var(--color-primary)" }}
            onClick={() => navigate(ROUTES.addressBook.contactDetail(String(row.id)))}
          >
            {row.name}
          </span>
        ),
      },
      {
        header: "Office",
        accessor: "officeName",
        render: (_value: unknown, row: Contact) => {
          if (row.officeId && row.officeName) {
            return (
              <span
                style={{ cursor: "pointer", color: "var(--color-primary)" }}
                onClick={e => {
                  e.stopPropagation();
                  navigate(ROUTES.addressBook.officeDetail(String(row.officeId)));
                }}
              >
                {row.officeName}
              </span>
            );
          }
          return "—";
        },
      },
      {
        header: "Job Title",
        accessor: "jobTitle",
        render: (value: unknown) => (value as string) || "-",
      },
      {
        header: "Email",
        accessor: "email",
        render: (value: unknown) => (value as string) || "-",
      },
    ];

    return (
      <div className={styles.section}>
        {contactsLoading ? (
          <LoadingState message="Loading contacts..." />
        ) : contactsError ? (
          <span className={styles.errorText}>{contactsError}</span>
        ) : (
          <DataTable
            data={companyContacts}
            columns={contactColumns}
            onRowClick={contact => navigate(ROUTES.addressBook.contactDetail(String(contact.id)))}
            rowKey="id"
            emptyMessage="No contacts associated with this company."
          />
        )}
      </div>
    );
  }

  function renderTabContent() {
    switch (activeTab) {
      case "details":
        return renderDetailsTab();
      case "offices":
        return renderOfficesTab();
      case "contacts":
        return renderContactsTab();
      case "ext-refs":
        return company ? (
          <ExternalReferencesPanel
            entityType="company"
            entityId={company.id}
            canWrite={canWrite}
            addableRefTypes={[
              "mondaq_id",
              "firm_ref",
              "crm_entity_number",
              "wordpress_term_id",
              "address_book",
            ]}
            validators={{ mondaq_id: validateMondaqValue }}
          />
        ) : null;
      case "relationships":
        return (
          <CompanyRelationships companyId={id!} businessType={company?.businessType ?? null} />
        );
      default:
        return renderDetailsTab();
    }
  }

  return (
    <div className={styles.detailWrapper}>
      <div>
        <PageHeader
          title={isNew ? "Create Company" : company?.name || "Company Details"}
          subtitle={
            isNew ? "Add a new company to the address book" : "View and edit company information"
          }
          meta={
            !isNew && company ? (
              <div className={styles.metaStrip}>
                <span className={styles.metaItem}>
                  <span className={styles.metaLabel}>WP Term ID</span>
                  <span className={styles.metaValue}>{company.companyId ?? "—"}</span>
                </span>
                {company.crmEntityNumber && (
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>CRM</span>
                    <span className={styles.metaValue}>{company.crmEntityNumber}</span>
                  </span>
                )}
                {company.firmRef && (
                  <span className={styles.metaItem}>
                    <span className={styles.metaLabel}>Firm Ref</span>
                    <span className={styles.metaValue}>{company.firmRef}</span>
                  </span>
                )}
              </div>
            ) : undefined
          }
        >
          <div style={{ display: "flex", gap: "12px" }}>
            <Button variant="outlined" color="default" onClick={handleBack}>
              <Icon icon="arrow-left" size="small" />
              Back
            </Button>
            {!isNew && activeTab === "offices" && isLive && (
              <PermissionGate permission="addressbook.write">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(ROUTES.addressBook.companyOfficeNew(id!))}
                >
                  Add Office
                </Button>
              </PermissionGate>
            )}
            {!isNew && activeTab === "contacts" && isLive && (
              <PermissionGate permission="addressbook.write">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(ROUTES.addressBook.companyContactNew(id!))}
                >
                  Create Contact
                </Button>
              </PermissionGate>
            )}
            {!isNew && isLive && (
              <PermissionGate permission="addressbook.write">
                <Button
                  variant="outlined"
                  color="default"
                  onClick={() => navigate(ROUTES.addressBook.companyMerge(id!))}
                >
                  Merge
                </Button>
                <Button
                  variant="outlined"
                  color="default"
                  onClick={() => navigate(ROUTES.addressBook.companyDemerge(id!))}
                >
                  Demerge
                </Button>
              </PermissionGate>
            )}
            {!isNew && isLive && (
              <PermissionGate permission="addressbook.delete">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  aria-label="Delete company"
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </Button>
              </PermissionGate>
            )}
          </div>
        </PageHeader>
      </div>

      {renderTabs()}

      {isNew ? renderDetailsTab() : renderTabContent()}

      {showDeleteModal && (
        <ConfirmationModal
          title="Delete Company"
          message={`Are you sure you want to delete "${company?.name}"?`}
          warningMessage="This action cannot be undone. This will permanently remove the company."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={5000}
        />
      )}
    </div>
  );
}

export default CompanyDetail;
